import Controller from "@ember/controller";
import { action } from "@ember/object";
import { alias, or, readOnly } from "@ember/object/computed";
import { ajax } from "discourse/lib/ajax";
import { wantsNewWindow } from "discourse/lib/intercept-click";
import DiscourseURL, { userPath } from "discourse/lib/url";
import { getWebauthnCredential } from "discourse/lib/webauthn";
import PasswordValidation from "discourse/mixins/password-validation";
import { SECOND_FACTOR_METHODS } from "discourse/models/user";
import getURL from "discourse-common/lib/get-url";
import discourseComputed from "discourse-common/utils/decorators";
import I18n from "discourse-i18n";
export default Controller.extend(PasswordValidation, dt7948.p({
  isDeveloper: alias("model.is_developer"),
  admin: alias("model.admin"),
  secondFactorRequired: alias("model.second_factor_required"),
  securityKeyRequired: alias("model.security_key_required"),
  backupEnabled: alias("model.backup_enabled"),
  securityKeyOrSecondFactorRequired: or("model.second_factor_required", "model.security_key_required"),
  otherMethodAllowed: readOnly("model.multiple_second_factor_methods"),
  passwordRequired: true,
  errorMessage: null,
  successMessage: null,
  requiresApproval: false,
  redirected: false,
  maskPassword: true,
  displaySecurityKeyForm(securityKeyRequired, selectedSecondFactorMethod) {
    return securityKeyRequired && selectedSecondFactorMethod === SECOND_FACTOR_METHODS.SECURITY_KEY;
  },
  initSelectedSecondFactorMethod() {
    if (this.model.security_key_required) {
      this.set("selectedSecondFactorMethod", SECOND_FACTOR_METHODS.SECURITY_KEY);
    } else if (this.model.second_factor_required) {
      this.set("selectedSecondFactorMethod", SECOND_FACTOR_METHODS.TOTP);
    } else if (this.model.backup_enabled) {
      this.set("selectedSecondFactorMethod", SECOND_FACTOR_METHODS.BACKUP_CODE);
    }
  },
  continueButtonText() {
    return I18n.t("password_reset.continue", {
      site_name: this.siteSettings.title
    });
  },
  redirectHref(redirectTo) {
    return getURL(redirectTo || "/");
  },
  lockImageUrl: getURL("/images/lock.svg"),
  done(event) {
    if (wantsNewWindow(event)) {
      return;
    }
    event.preventDefault();
    this.set("redirected", true);
    DiscourseURL.redirectTo(this.redirectTo || "/");
  },
  togglePasswordMask() {
    this.toggleProperty("maskPassword");
  },
  async submit() {
    try {
      const result = await ajax({
        url: userPath(`password-reset/${this.get("model.token")}.json`),
        type: "PUT",
        data: {
          password: this.accountPassword,
          second_factor_token: this.securityKeyCredential || this.secondFactorToken,
          second_factor_method: this.selectedSecondFactorMethod,
          timezone: moment.tz.guess()
        }
      });
      if (result.success) {
        this.set("successMessage", result.message);
        this.set("redirectTo", result.redirect_to);
        if (result.requires_approval) {
          this.set("requiresApproval", true);
        } else {
          this.set("redirected", true);
          DiscourseURL.redirectTo(result.redirect_to || "/");
        }
      } else {
        if (result.errors.security_keys || result.errors.user_second_factors) {
          this.setProperties({
            secondFactorRequired: this.secondFactorRequired,
            securityKeyRequired: this.securityKeyRequired,
            password: null,
            errorMessage: result.message
          });
        } else if (this.secondFactorRequired || this.securityKeyRequired) {
          this.setProperties({
            secondFactorRequired: false,
            securityKeyRequired: false,
            errorMessage: null
          });
        } else if (result.errors?.password?.length > 0) {
          this.rejectedPasswords.pushObject(this.accountPassword);
          this.rejectedPasswordsMessages.set(this.accountPassword, (result.friendly_messages || []).join("\n"));
        }
        if (result.message) {
          this.set("errorMessage", result.message);
        }
      }
    } catch (e) {
      if (e.jqXHR?.status === 429) {
        this.set("errorMessage", I18n.t("user.second_factor.rate_limit"));
      } else {
        throw new Error(e);
      }
    }
  },
  authenticateSecurityKey() {
    this.set("selectedSecondFactorMethod", SECOND_FACTOR_METHODS.SECURITY_KEY);
    getWebauthnCredential(this.model.challenge, this.model.allowed_credential_ids, credentialData => {
      this.set("securityKeyCredential", credentialData);
      this.send("submit");
    }, errorMessage => {
      this.setProperties({
        securityKeyRequired: true,
        password: null,
        errorMessage
      });
    });
  }
}, [["method", "displaySecurityKeyForm", [discourseComputed("securityKeyRequired", "selectedSecondFactorMethod")]], ["method", "continueButtonText", [discourseComputed()]], ["method", "redirectHref", [discourseComputed("redirectTo")]], ["method", "done", [action]], ["method", "togglePasswordMask", [action]], ["method", "submit", [action]], ["method", "authenticateSecurityKey", [action]]]));